import { forwardRef, useState } from 'react';
import * as React from 'react';
import TextareaAutosize, { TextareaAutosizeProps } from 'react-textarea-autosize';
import styled from 'styled-components';
import {
    disabledText,
    inputBorder,
    inputBorderFocused,
    inputBorderInvalid,
    primaryText,
} from 'styles/theme';
import { SecondaryText } from 'styles/theme_styles/text';
import { mergeRefs } from 'utils/mergeRefs';

const StyledTextarea = styled(TextareaAutosize)`
    box-sizing: border-box;
    padding: 1rem;
    width: 100%;
    resize: vertical;
    min-height: 4rem;
    max-height: 50rem;
    background: transparent;
    border: 2px solid ${inputBorder};
    color: ${primaryText};

    &:focus {
        border-color: ${inputBorderFocused};
        outline: none;
    }

    &:disabled {
        color: ${disabledText};
    }

    &[aria-invalid='true'] {
        border-color: ${inputBorderInvalid};
    }
`;

type TextareaProps = TextareaAutosizeProps &
    Readonly<{
        showLengthIndicator?: boolean;
    }>;

const Textarea = (
    { showLengthIndicator, maxLength, value, onChange, ...rest }: TextareaProps,
    ref: React.Ref<HTMLTextAreaElement>
) => {
    const [length, setLength] = useState(value?.toString().length ?? 0);

    const onTextChange: React.ChangeEventHandler<HTMLTextAreaElement> = (event) => {
        setLength(event.target.value.length);
        onChange?.(event);
    };

    return (
        <div>
            <StyledTextarea
                maxLength={maxLength}
                value={value}
                onChange={onTextChange}
                {...rest}
                ref={mergeRefs(ref, (el) => setLength(el?.value.length ?? 0))}
            />
            {showLengthIndicator && maxLength && (
                <SecondaryText className="f-12 flex-row flex-grow-1 justify-flex-end">
                    {length} / {maxLength}
                </SecondaryText>
            )}
        </div>
    );
};

const forwardRefTextarea = forwardRef(Textarea);
export { forwardRefTextarea as Textarea };
